import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, Outlet, useLocation, useParams } from 'react-router';
import { applyLanguage, detectLanguage, ensureLocale, isLang } from '@/i18n';
import { PageSpinner, SiteLayout } from '@/layouts/SiteLayout';
import { useSession } from '@/features/auth/session';
import { ErrorState } from '@/components/ui/primitives';

const HomePage = lazy(() => import('@/pages/HomePage'));
const AboutPage = lazy(() => import('@/pages/AboutPage'));
const CataloguePage = lazy(() => import('@/pages/CataloguePage'));
const ProductPage = lazy(() => import('@/pages/ProductPage'));
const VehicleFinderPage = lazy(() => import('@/pages/VehicleFinderPage'));
const BrandsPage = lazy(() => import('@/pages/BrandsPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const AccountPage = lazy(() => import('@/pages/AccountPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const AdminApp = lazy(() => import('@/pages/admin/AdminApp'));

/** Validates the :lang segment and applies language + direction (RTL for ar/he). */
function LanguageGate() {
  const { lang } = useParams();
  const location = useLocation();
  const valid = isLang(lang);
  useEffect(() => {
    if (!valid) return;
    let cancelled = false;
    void ensureLocale(lang).then(() => !cancelled && applyLanguage(lang));
    return () => {
      cancelled = true;
    };
  }, [valid, lang]);
  if (!valid) return <Navigate to={`/${detectLanguage()}${location.pathname}${location.search}`} replace />;
  return <Outlet />;
}

function RootRedirect() {
  const location = useLocation();
  return <Navigate to={`/${detectLanguage()}${location.search}`} replace />;
}

function RequireAuth({ role, children }: { role?: 'ADMIN'; children: ReactNode }) {
  const { lang } = useParams();
  const location = useLocation();
  const session = useSession();
  if (session.isPending) return <PageSpinner />;
  if (session.isError) return <div className="mx-auto max-w-xl px-4 py-24"><ErrorState error={session.error} onRetry={() => void session.refetch()} /></div>;
  if (!session.data) return <Navigate to={`/${lang}/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (role && session.data.role !== role) return <Navigate to={`/${lang}/account`} replace />;
  return <>{children}</>;
}

function RouteError() {
  return (
    <div className="mx-auto max-w-xl px-4 py-24">
      <ErrorState error={null} onRetry={() => window.location.reload()} />
    </div>
  );
}

export const router = createBrowserRouter([
  { path: '/', element: <RootRedirect />, errorElement: <RouteError /> },
  {
    path: '/:lang',
    element: <LanguageGate />,
    errorElement: <RouteError />,
    children: [
      {
        path: 'admin/*',
        element: (
          <RequireAuth role="ADMIN">
            <Suspense fallback={<PageSpinner />}>
              <AdminApp />
            </Suspense>
          </RequireAuth>
        ),
      },
      {
        element: <SiteLayout />,
        children: [
          { index: true, element: <HomePage /> },
          { path: 'about', element: <AboutPage /> },
          { path: 'catalogue', element: <CataloguePage /> },
          { path: 'catalogue/:slug', element: <ProductPage /> },
          { path: 'vehicle', element: <VehicleFinderPage /> },
          { path: 'brands', element: <BrandsPage /> },
          { path: 'contact', element: <ContactPage /> },
          { path: 'login', element: <LoginPage /> },
          {
            path: 'account',
            element: (
              <RequireAuth>
                <AccountPage />
              </RequireAuth>
            ),
          },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
